import { createContext, ReactNode, use } from 'react';

const noOp = () => {};

interface LayoutContextValue {
    isReader?: boolean;
    presentation: Presentation;
    menuOpened: boolean;
    menuOverContent: boolean;
    contentBreakpoint?: string | null;
    contentWidth?: number | null;
    openMenu: () => void;
    closeMenu: () => void;
    brand?: Brand | null;
}

export const LayoutContext = createContext<LayoutContextValue>({
    isReader: false,
    presentation: 'default',
    menuOpened: false,
    menuOverContent: false,
    contentBreakpoint: null,
    contentWidth: null,
    openMenu: noOp,
    closeMenu: noOp,
    brand: null,
});

export function useLayoutContext() {
    const context = use(LayoutContext);
    return context;
}

export function useMenu() {
    const {
        menuOpened: opened,
        openMenu: open,
        closeMenu: close,
        menuOverContent: overContent,
    } = useLayoutContext();
    return {
        opened,
        overContent,
        open,
        close,
    };
}

export function useGlobalBrand() {
    const { brand = null } = useLayoutContext();
    return brand;
}

export function useIsReader() {
    const { presentation = 'default' } = useLayoutContext();
    return presentation === 'reader';
}

export function useIsCapture() {
    const { presentation = 'default' } = useLayoutContext();
    return presentation === 'capture';
}

export function usePresentation() {
    const { presentation = 'default' } = useLayoutContext();
    return presentation;
}

interface LayoutContextProviderProps {
    presentation?: Presentation;
    menuOpened?: boolean;
    openMenu?: () => void;
    closeMenu?: () => void;
    menuOverContent?: boolean;
    brand?: Brand | null;
    children?: ReactNode;
}

export function LayoutContextProvider({
    presentation = 'default',
    menuOpened = false,
    openMenu = noOp,
    closeMenu = noOp,
    menuOverContent = false,
    brand = null,
    children = null,
}: LayoutContextProviderProps) {
    const value = {
        presentation,
        menuOpened,
        openMenu,
        closeMenu,
        menuOverContent,
        brand,
    };

    return <LayoutContext value={value}>{children}</LayoutContext>;
}
